/**
 * 邮寄地址页面
 * 用于实体卡申请时填写/编辑邮寄地址
 * 进入时拉取列表：若有地址则回填第一条供编辑，保存时调用 add 接口覆盖
 */

import { useState, useCallback, useMemo, useEffect, type ChangeEvent } from 'react'

// 判断是否为中文地区（根据国家名称匹配）
const isChineseRegionByName = (country: string) => {
    if (!country) return false
    const lower = country.toLowerCase()
    return lower.includes('china') || lower.includes('taiwan') || lower.includes('hong kong') || lower.includes('macao') || lower.includes('macau')
        || ['cn', 'tw', 'hk', 'mo'].includes(lower)
}
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import CustomTextInput from '@/components/CustomTextInput'
import CountrySelector from '@/components/CountrySelector'
import CountryCodeSelector from '@/components/CountryCodeSelector'
import { Toast } from 'react-vant'
import { APIWeb } from '@/api'

// 表单数据类型
interface ShippingAddressForm {
    country: string       // 国家/地区
    firstName: string     // 名
    lastName: string      // 姓
    phone: string         // 电话号码
    addressLine1: string  // 详细地址
    addressLine2: string  // 地址第二行
    state: string         // 省/区
    city: string          // 城市
    zipCode: string       // 邮编
}

// 将 API 返回的地址转为表单数据
function addressToForm(addr: ShippingAddress): ShippingAddressForm {
    return {
        country: addr.country ?? '',
        firstName: addr.first_name ?? '',   // first_name = 名
        lastName: addr.last_name ?? '',     // last_name = 姓
        phone: addr.phone_number ?? '',
        addressLine1: addr.address_line1 ?? '',
        addressLine2: addr.address_line2 ?? '',
        state: addr.province ?? '',
        city: addr.city ?? '',
        zipCode: addr.postal_code ?? ''
    }
}

export default function AddShippingAddress() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()

    // 国家区号
    const [countryCode, setCountryCode] = useState('+86')

    // 表单数据
    const [formData, setFormData] = useState<ShippingAddressForm>({
        country: '',
        firstName: '',
        lastName: '',
        phone: '',
        addressLine1: '',
        addressLine2: '',
        state: '',
        city: '',
        zipCode: ''
    })

    // 列表加载中
    const [loadingList, setLoadingList] = useState(true)

    // 表单校验错误
    const [errors, setErrors] = useState<Partial<Record<keyof ShippingAddressForm, string>>>({})

    // 手机号验证正则（6-15位数字）
    const PHONE_REGEX = /^\d{6,15}$/

    // 进入页面获取邮寄地址列表，有则回填第一条供编辑
    const fetchList = useCallback(async () => {
        try {
            setLoadingList(true)
            const response = await APIWeb.getShippingAddress()
            let addressList: ShippingAddress[] = []
            if (Array.isArray(response)) {
                addressList = response
            } else if (response?.data && Array.isArray(response.data)) {
                addressList = response.data
            }
            if (addressList.length > 0) {
                const first = addressList[0]
                setFormData(addressToForm(first))
                setCountryCode(first.phone_code ?? '+86')
            }
        } catch (e) {
            console.error('获取邮寄地址列表失败:', e)
        } finally {
            setLoadingList(false)
        }
    }, [])

    useEffect(() => {
        fetchList()
    }, [fetchList])

    // 港/澳/台/大陆：placeholder 为中文；否则为英文（label 仍随应用语言 t()）
    const isChinese = useMemo(() => isChineseRegionByName(formData.country), [formData.country])
    const placeholderLocale = useMemo(() => {
        if (!isChinese) return 'en'
        const lang = i18n.language || ''
        if (lang.startsWith('zh')) return lang
        return 'zh-CN'
    }, [isChinese, i18n.language])
    const placeholderT = useMemo(() => i18n.getFixedT(placeholderLocale), [i18n, placeholderLocale])

    // 顶部说明：仅随系统/应用语言在中文与英文间切换（非中文界面统一用英文文案）
    const bannerLocale = useMemo(() => {
        const lang = i18n.language || ''
        if (lang.startsWith('zh')) return lang
        return 'en'
    }, [i18n.language])
    const bannerT = useMemo(() => i18n.getFixedT(bannerLocale), [i18n, bannerLocale])

    // 根据国家/地区动态设置姓名 placeholder
    // 中国/港/台/澳：强制中文"张"/"三"；其他地区：统一英文"Zhang"/"San"（国家选择优先级 > 系统语言）
    const lastNamePlaceholder = useMemo(() => isChinese ? '张' : 'Zhang', [isChinese])
    const firstNamePlaceholder = useMemo(() => isChinese ? '三' : 'San', [isChinese])

    const addressLineExamplePlaceholder = useMemo(
        () =>
            isChinese
                ? '例:龙华区金龙路89号置地花园3栋702室'
                : placeholderT('例X', { example: '1 Infinite loop cupertion CA 95014' }),
        [isChinese, placeholderT]
    )

    // 手机号格式校验
    const phoneError = useMemo(() => {
        if (!formData.phone) return ''
        return PHONE_REGEX.test(formData.phone) ? '' : t('请输入有效的手机号X位数字')
    }, [formData.phone, t])

    // 提交加载状态
    const [loading, setLoading] = useState(false)

    // 更新表单字段
    const updateField = useCallback((field: keyof ShippingAddressForm, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        // 清除该字段的错误
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }))
        }
    }, [errors])

    // 手机号输入处理（只允许输入数字）
    const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const numericValue = e.target.value.replace(/[^\d]/g, '')
        updateField('phone', numericValue)
    }, [updateField])

    // 选择国家
    const handleCountryChange = useCallback((value: string) => {
        updateField('country', value)
    }, [updateField])

    // 表单校验
    const validateForm = useCallback((): boolean => {
        const newErrors: Partial<Record<keyof ShippingAddressForm, string>> = {}

        if (!formData.country) newErrors.country = t('请选择国家或地区')
        if (!formData.firstName) newErrors.firstName = t('请输入名')
        if (!formData.lastName) newErrors.lastName = t('请输入姓')
        if (!formData.phone) {
            newErrors.phone = t('请输入电话号码')
        } else if (!PHONE_REGEX.test(formData.phone)) {
            newErrors.phone = t('请输入有效的手机号X位数字')
        }
        if (!formData.addressLine1) newErrors.addressLine1 = t('请输入详细地址')
        if (!formData.city) newErrors.city = t('请输入城市')
        if (!formData.zipCode) newErrors.zipCode = t('请输入邮编')

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }, [formData, t])

    // 检查表单是否填写完整
    const isFormValid = useMemo(() => {
        return (
            formData.country &&
            formData.firstName &&
            formData.lastName &&
            formData.phone &&
            PHONE_REGEX.test(formData.phone) &&
            formData.addressLine1 &&
            formData.city &&
            formData.zipCode
        )
    }, [formData])

    // 提交表单
    const handleSubmit = useCallback(async () => {
        if (loading) return

        if (!validateForm()) {
            Toast.info(t('请完善表单信息'))
            return
        }

        try {
            setLoading(true)

            // 构建请求参数
            const params: AddShippingAddressRequest = {
                country: formData.country,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone_code: countryCode,
                phone_number: formData.phone,
                address_line1: formData.addressLine1,
                address_line2: formData.addressLine2 || undefined,
                province: formData.state,
                city: formData.city,
                postal_code: formData.zipCode,
            }

            // 调用保存邮寄地址接口
            await APIWeb.addShippingAddress(params)

            Toast.success(t('保存成功'))
            // 返回上一页
            navigate(-1)
        } catch (error: any) {
            console.error('保存邮寄地址失败:', error)
            const errorMsg = error?.response?.data?.message || error?.message || t('保存失败')
            Toast.fail(errorMsg)
        } finally {
            setLoading(false)
        }
    }, [formData, countryCode, validateForm, navigate, t, loading])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={
                    <span className="text-base font-medium">{t('邮寄地址')}</span>
                }
            />

            <div className="px-5 py-6 pb-22">
                {/* 提示信息 */}
                <div className="bg-[#F0F2FF] rounded-xl px-4 py-3 mb-6 flex items-start gap-2">
                    <span className="text-sm text-[var(--primary-color)]">
                        {bannerT(isChinese ? '表单只能用中文填写' : '表单只能用英文填写')}
                    </span>
                </div>

                <div className="space-y-4">
                    {/* 国家/地区 */}
                    <div>
                        <div className="block text-sm text-[#667085] mb-2 font-medium">{t('银行所在国家或地区')}</div>
                        <CountrySelector
                            value={formData.country}
                            onChange={handleCountryChange}
                            placeholder={placeholderT('银行所在国家或地区')}
                            returnType="value"
                        />
                        {errors.country && <div className="text-xs text-red-500 mt-1">{errors.country}</div>}
                    </div>

                    {/* 姓 + 名 同一行 */}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <div className="block text-sm text-[#667085] mb-2 font-medium">{t('姓氏')}</div>
                            <CustomTextInput
                                value={formData.lastName}
                                onChangeText={(text) => updateField('lastName', text)}
                                placeholder={lastNamePlaceholder}
                                isErrored={!!errors.lastName}
                            />
                            {errors.lastName && <div className="text-xs text-red-500 mt-1">{errors.lastName}</div>}
                        </div>
                        <div className="flex-1">
                            <div className="block text-sm text-[#667085] mb-2 font-medium">{t('名字')}</div>
                            <CustomTextInput
                                value={formData.firstName}
                                onChangeText={(text) => updateField('firstName', text)}
                                placeholder={firstNamePlaceholder}
                                isErrored={!!errors.firstName}
                            />
                            {errors.firstName && <div className="text-xs text-red-500 mt-1">{errors.firstName}</div>}
                        </div>
                    </div>

                    {/* 电话号码 */}
                    <div>
                        <div className="block text-sm text-[#667085] mb-2 font-medium">{t('电话号码')}</div>
                        <div className={`flex items-center h-12 rounded-2.5 border border-solid bg-white ${(phoneError || errors.phone) ? 'border-red-500' : 'border-[#EAECF0]'}`}>
                            <div className="h-full flex items-center pl-4 pr-2 [&>div]:border-none [&>div]:h-auto [&>div]:p-0 [&>div]:rounded-none cursor-pointer">
                                <CountryCodeSelector
                                    value={countryCode}
                                    onChange={setCountryCode}
                                />
                            </div>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={handlePhoneChange}
                                placeholder={placeholderT('输入手机号')}
                                inputMode="numeric"
                                className="flex-1 h-full px-3 outline-none text-[#1D2129] placeholder-[#C9CDD4] bg-transparent"
                            />
                        </div>
                        {(phoneError || errors.phone) && <div className="text-xs text-red-500 mt-1">{phoneError || errors.phone}</div>}
                    </div>

                    {/* 详细地址 */}
                    <div>
                        <div className="block text-sm text-[#667085] mb-2 font-medium">{t('地址')}</div>
                        <CustomTextInput
                            value={formData.addressLine1}
                            onChangeText={(text) => updateField('addressLine1', text)}
                            placeholder={addressLineExamplePlaceholder}
                            isErrored={!!errors.addressLine1}
                        />
                        {errors.addressLine1 && <div className="text-xs text-red-500 mt-1">{errors.addressLine1}</div>}
                    </div>

                    {/* 地址第二行 */}
                    <div>
                        <div className="block text-sm text-[#667085] mb-2 font-medium">{t('地址第二行')}</div>
                        <CustomTextInput
                            value={formData.addressLine2}
                            onChangeText={(text) => updateField('addressLine2', text)}
                            placeholder={addressLineExamplePlaceholder}
                        />
                    </div>

                    {/* 省/区 */}
                    <div>
                        <div className="block text-sm text-[#667085] mb-2 font-medium">{t('省或区')}</div>
                        <CustomTextInput
                            value={formData.state}
                            onChangeText={(text) => updateField('state', text)}
                            placeholder={placeholderT('请输入省/区')}
                            isErrored={!!errors.state}
                        />
                        {errors.state && <div className="text-xs text-red-500 mt-1">{errors.state}</div>}
                    </div>

                    {/* 城市 */}
                    <div>
                        <div className="block text-sm text-[#667085] mb-2 font-medium">{t('城市')}</div>
                        <CustomTextInput
                            value={formData.city}
                            onChangeText={(text) => updateField('city', text)}
                            placeholder={placeholderT('例X', { example: 'Frankfurt' })}
                            isErrored={!!errors.city}
                        />
                        {errors.city && <div className="text-xs text-red-500 mt-1">{errors.city}</div>}
                    </div>



                    {/* 邮编 */}
                    <div>
                        <div className="block text-sm text-[#667085] mb-2 font-medium">{t('邮政编码')}</div>
                        <CustomTextInput
                            value={formData.zipCode}
                            onChangeText={(text) => updateField('zipCode', text)}
                            placeholder={placeholderT('例X', { example: '100009' })}
                            isErrored={!!errors.zipCode}
                        />
                        {errors.zipCode && <div className="text-xs text-red-500 mt-1">{errors.zipCode}</div>}
                    </div>
                </div>
            </div>

            <FixedBottom className="bg-white">
                <CustomButton
                    onClick={handleSubmit}
                    disabled={!isFormValid || loading || loadingList}
                    loading={loading}
                >
                    {t('储存')}
                </CustomButton>
            </FixedBottom>
        </PageLayout>
    )
}

